import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { getMonthWeekInfo } from '@/util';

import type { BarChartData } from '../type';

export const formatXAxisLabel = (
  timestamp: string,
  periodUnit: 'day' | 'week' | 'month',
) => {
  const date = dayjs(timestamp);

  switch (periodUnit) {
    case 'day':
      return date.format('MM-DD');
    case 'week': {
      const { year, month, thisMonthWeekNum: week } = getMonthWeekInfo(date);
      return `${year}Y ${month}M ${week}W`;
    }
    case 'month':
      return `${date.year()}Y ${date.month() + 1}M`;
    default:
      return date.format('MM-DD');
  }
};

/**
 * timestamp를 정규화합니다 (비교를 위해)
 * periodUnit에 따라 적절한 형식으로 변환합니다.
 */
export const normalizeTimestamp = (
  timestamp: string,
  periodUnit: 'day' | 'week' | 'month',
): string => {
  const date = dayjs(timestamp);

  if (periodUnit === 'day') {
    return date.startOf('day').toISOString();
  } else if (periodUnit === 'week') {
    const weekInfo = getMonthWeekInfo(date);
    return weekInfo.thisWeekMonday.startOf('day').toISOString();
  } else if (periodUnit === 'month') {
    return date.startOf('month').toISOString();
  }
  return date.toISOString();
};

/**
 * 데이터를 정규화된 timestamp를 키로 하는 Map으로 변환합니다.
 */
export const createDataMap = (
  data: BarChartData[],
  periodUnit: 'day' | 'week' | 'month',
): Map<string, BarChartData> => {
  const dataMap = new Map<string, BarChartData>();
  data.forEach(item => {
    const normalizedKey = normalizeTimestamp(item.timeStamp, periodUnit);
    dataMap.set(normalizedKey, item);
  });
  return dataMap;
};

/**
 * label의 color 정보를 수집합니다.
 */
export const createLabelInfoMap = (
  data: BarChartData[],
): Map<string, { aiCount: number; humanCount: number; color: string }> => {
  if (data.length === 0) return new Map();

  const firstData = data[0];
  const labelNames = Object.keys(firstData.labelData);
  const labelInfoMap = new Map<
    string,
    { aiCount: number; humanCount: number; color: string }
  >();

  data.forEach(item => {
    labelNames.forEach(labelName => {
      if (item.labelData[labelName] && !labelInfoMap.has(labelName)) {
        labelInfoMap.set(labelName, {
          aiCount: 0,
          humanCount: 0,
          color: item.labelData[labelName].color,
        });
      }
    });
  });

  return labelInfoMap;
};

/**
 * between 사이의 모든 기간을 생성합니다.
 */
export const generateAllPeriods = (
  periodUnit: 'day' | 'week' | 'month',
  between: [Dayjs, Dayjs],
): string[] => {
  const allPeriods: string[] = [];
  const [start, end] = between;

  if (periodUnit === 'day') {
    let current = start.startOf('day');
    const endDate = end.endOf('day');
    while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
      allPeriods.push(current.toISOString());
      current = current.add(1, 'day');
    }
  } else if (periodUnit === 'week') {
    const startWeekInfo = getMonthWeekInfo(start);
    let current = startWeekInfo.thisWeekMonday.startOf('day');
    const endWeekInfo = getMonthWeekInfo(end);
    const endWeekMonday = endWeekInfo.thisWeekMonday.startOf('day');

    while (current.isBefore(endWeekMonday) || current.isSame(endWeekMonday)) {
      allPeriods.push(current.toISOString());
      current = current.add(1, 'week');
    }
  } else if (periodUnit === 'month') {
    let current = start.startOf('month');
    const endMonth = end.endOf('month');
    while (current.isBefore(endMonth) || current.isSame(endMonth, 'month')) {
      allPeriods.push(current.toISOString());
      current = current.add(1, 'month');
    }
  }

  return allPeriods;
};

/**
 * 기간 목록을 BarChartData로 매핑합니다.
 */
export const mapPeriodsToData = (
  allPeriods: string[],
  dataMap: Map<string, BarChartData>,
  labelInfoMap: Map<
    string,
    { aiCount: number; humanCount: number; color: string }
  >,
  periodUnit: 'day' | 'week' | 'month',
): BarChartData[] => {
  const labelNames = Array.from(labelInfoMap.keys());

  return allPeriods.map(timestamp => {
    const normalizedTimestamp = normalizeTimestamp(timestamp, periodUnit);
    const existingData = dataMap.get(normalizedTimestamp);

    if (existingData) return existingData;

    const emptyLabelData: BarChartData['labelData'] = {};
    labelNames.forEach(labelName => {
      const labelInfo = labelInfoMap.get(labelName);
      emptyLabelData[labelName] = {
        aiCount: 0,
        humanCount: 0,
        color: labelInfo?.color || '#000000',
      };
    });

    return {
      timeStamp: timestamp,
      labelData: emptyLabelData,
    };
  });
};

/**
 * periodUnit과 between을 고려하여 빈 날짜를 채운 데이터를 생성합니다.
 */
export const fillMissingDates = (
  data: BarChartData[],
  periodUnit: 'day' | 'week' | 'month',
  between: [Dayjs, Dayjs],
): BarChartData[] => {
  if (data.length === 0) return [];

  const dataMap = createDataMap(data, periodUnit);
  const labelInfoMap = createLabelInfoMap(data);
  const allPeriods = generateAllPeriods(periodUnit, between);

  return mapPeriodsToData(allPeriods, dataMap, labelInfoMap, periodUnit);
};
